import { TestTube2, Sparkles, ArrowRightLeft, Crown, Wrench, Droplets, ShieldCheck, Filter, Sun, FlaskConical, FileText, ExternalLink } from "lucide-react";
import { formatDateAU } from "@/lib/formatDateAU";

const EVENT_ICONS = {
  test_published:           { icon: TestTube2,      color: "bg-emerald-100 text-emerald-700" },
  test_approved:            { icon: TestTube2,      color: "bg-cyan-100 text-cyan-700" },
  test_awaiting_review:     { icon: TestTube2,      color: "bg-amber-100 text-amber-700" },
  test_results_entered:     { icon: TestTube2,      color: "bg-amber-100 text-amber-700" },
  test_received:            { icon: TestTube2,      color: "bg-slate-100 text-slate-700" },
  test_pending_sample:      { icon: TestTube2,      color: "bg-slate-100 text-slate-500" },

  ai_summary_approved:      { icon: Sparkles,       color: "bg-cyan-100 text-cyan-700" },

  property_transferred:     { icon: ArrowRightLeft, color: "bg-amber-100 text-amber-700" },

  membership_started:       { icon: Crown,          color: "bg-cyan-100 text-cyan-700" },
  membership_renewed:       { icon: Crown,          color: "bg-emerald-100 text-emerald-700" },
  membership_cancelled:     { icon: Crown,          color: "bg-rose-100 text-rose-700" },

  tank_clean:               { icon: Wrench,         color: "bg-blue-100 text-blue-700" },
  biocleanse_service:       { icon: Droplets,       color: "bg-cyan-100 text-cyan-700" },
  filtration_installation:  { icon: Filter,         color: "bg-violet-100 text-violet-700" },
  filter_replacement:       { icon: Filter,         color: "bg-violet-100 text-violet-700" },
  uv_replacement:           { icon: Sun,            color: "bg-amber-100 text-amber-700" },
  aeration_installation:    { icon: FlaskConical,   color: "bg-blue-100 text-blue-700" },
  recommendation_logged:    { icon: ShieldCheck,    color: "bg-slate-100 text-slate-700" },
  general_note:             { icon: FileText,       color: "bg-slate-100 text-slate-600" },
};

// Events whose underlying test has a customer-visible COA report PDF.
// `test_published` and `ai_summary_approved` both surface against a
// published test, so each can deep-link to the same per-test report.
const REPORT_READY_EVENTS = new Set(["test_published", "ai_summary_approved"]);
// Events that reference a test still in lab/admin review — we show a
// subtle "Report under review" affordance instead of a clickable CTA.
const REPORT_PENDING_EVENTS = new Set([
  "test_approved",
  "test_awaiting_review",
  "test_results_entered",
  "test_received",
  "test_pending_sample",
]);

function formatRelative(iso) {
  return formatDateAU(iso);
}

export function PropertyTimeline({
  events,
  title = "Property timeline",
  emptyMessage = "No events yet — your timeline grows as we test and service your property.",
  onDelete = null,
  onOpenReport = null,
}) {
  if (!events) return null;
  return (
    <div data-testid="property-timeline">
      <h2 className="font-display text-xl font-bold text-slate-900 mb-3">{title}</h2>
      {events.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-200 p-7 text-center text-sm text-slate-500">{emptyMessage}</div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-200 p-6 lg:p-7">
          <ol className="relative space-y-5">
            {events.map((ev, idx) => {
              const cfg = EVENT_ICONS[ev.event_type] || { icon: FileText, color: "bg-slate-100 text-slate-600" };
              const Icon = cfg.icon;
              const isLast = idx === events.length - 1;
              const testId = ev?.metadata?.test_id;
              const canOpenReport =
                !!onOpenReport && !!testId && REPORT_READY_EVENTS.has(ev.event_type);
              const reportPending =
                !canOpenReport && !!testId && REPORT_PENDING_EVENTS.has(ev.event_type);
              return (
                <li key={ev.id} data-testid={`timeline-event-${ev.event_type}`} className="relative flex gap-4">
                  {!isLast && <span className="absolute left-[15px] top-9 bottom-[-20px] w-px bg-slate-200" aria-hidden />}
                  <div className={`relative z-10 w-[31px] h-[31px] rounded-full flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0 pb-1.5">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <h3 className="font-display font-semibold text-slate-900 text-[15px] leading-snug">{ev.title}</h3>
                      <time className="text-[11px] text-slate-400 font-mono">{formatRelative(ev.occurred_at)}</time>
                    </div>
                    {ev.description && <p className="mt-0.5 text-sm text-slate-600 leading-relaxed">{ev.description}</p>}
                    <div className="mt-1 flex flex-wrap items-center gap-3">
                      {canOpenReport && (
                        <button
                          type="button"
                          onClick={() => onOpenReport(ev)}
                          data-testid={`timeline-open-report-${ev.id}`}
                          className="inline-flex items-center gap-1 text-[12px] font-semibold text-cyan-700 hover:text-cyan-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-1 rounded"
                          aria-label="Open water test report PDF"
                        >
                          View report
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      )}
                      {reportPending && (
                        <span
                          data-testid={`timeline-report-pending-${ev.id}`}
                          className="inline-flex items-center text-[11px] italic text-slate-400"
                        >
                          Report under review
                        </span>
                      )}
                      {onDelete && ev.source === "manual" && (
                        <button onClick={() => onDelete(ev)} className="text-[11px] text-rose-500 hover:text-rose-700">Remove</button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}
